import type {
  Machine,
  MachineMetrics,
  MachineRealTimeData,
} from "@/types/machines";
import { API_TIMEOUT_MS } from "@/config/apiTimeout";
import { t } from "@/i18n";
import {
  parseUtc,
  parseWallTime,
  resolveTimeZone,
  startOfDayInTz,
  toUtcSqlString,
} from "@/utils/deviceTime";
import { get, post, put, del } from "@/utils/axios";
import { httpClient } from "@/services/api";
import { authHttp } from "@/services/api/authHttp";

/**
 * `creation`/`modified` são carimbo do Frappe, gravado com `now_datetime()` no fuso
 * do SITE — a migração para UTC pegou só a telemetria (Device Data/State/Alarm...).
 * No mesmo registro convivem `date` em UTC e `creation` em hora de São Paulo. Lido
 * como UTC, o carimbo era convertido de novo para o fuso do site e aparecia 3h
 * ATRASADO (16:17 virava 13:17). Aqui é hora de parede: lê-se o que está gravado.
 */
function parseCarimboDoFrappe(valor?: string | null): Date | null {
  return parseWallTime(valor, resolveTimeZone(import.meta.env.VITE_SITE_TIMEZONE));
}

// ✅ Helper para buscar TODAS as relações Device→Sector de uma vez (com filtro opcional).
// Substitui N requisições (uma por máquina) por 1 única.
async function getAllDeviceSectorInfos(
  deviceIds: string[],
  company?: string,
): Promise<Map<string, { iot_sector: string; iot_company?: string }>> {
  const map = new Map<string, { iot_sector: string; iot_company?: string }>();
  if (!deviceIds || deviceIds.length === 0) return map;

  try {
    const filters: any[] = [["iot_device", "in", deviceIds]];
    if (company) filters.push(["iot_company", "=", company]);

    const response = await httpClient.get<{ data: any[] }>(
      "/api/resource/IoT%20Device%20Sector",
      {
        params: {
          fields: JSON.stringify(["iot_device", "iot_sector", "iot_company"]),
          filters: JSON.stringify(filters),
          limit_page_length: 0,
        },
        skipCompanyFilter: true,
      },
    );

    const data = Array.isArray(response?.data) ? response.data : [];
    data.forEach((row: any) => {
      if (row?.iot_device && row?.iot_sector) {
        map.set(row.iot_device, {
          iot_sector: row.iot_sector,
          iot_company: row.iot_company,
        });
      }
    });
  } catch (error) {
    console.warn("[Machines] Falha ao buscar IoT Device Sector em lote:", error);
  }
  return map;
}

// ✅ Bulk fetch dos setores referenciados.
async function getAllSectorsByIds(
  sectorIds: string[],
): Promise<
  Map<
    string,
    { name: string; iot_sector_name?: string; iot_manufacturing_plant?: string }
  >
> {
  const map = new Map<string, any>();
  if (!sectorIds || sectorIds.length === 0) return map;
  try {
    const response = await httpClient.get<{ data: any[] }>(
      "/api/resource/IoT%20Sector",
      {
        params: {
          fields: JSON.stringify([
            "name",
            "iot_sector_name",
            "iot_manufacturing_plant",
          ]),
          filters: JSON.stringify([["name", "in", sectorIds]]),
          limit_page_length: 0,
        },
        skipCompanyFilter: true,
      },
    );
    const data = Array.isArray(response?.data) ? response.data : [];
    data.forEach((row: any) => {
      if (row?.name) map.set(row.name, row);
    });
  } catch (error) {
    console.warn("[Machines] Falha ao buscar IoT Sector em lote:", error);
  }
  return map;
}

// ✅ Bulk fetch das plantas referenciadas.
async function getAllPlantsByIds(
  plantIds: string[],
): Promise<Map<string, { name: string; iot_manufacturing_plant_name?: string }>> {
  const map = new Map<string, any>();
  if (!plantIds || plantIds.length === 0) return map;
  try {
    const response = await httpClient.get<{ data: any[] }>(
      "/api/resource/IoT%20Manufacturing%20Plant",
      {
        params: {
          fields: JSON.stringify([
            "name",
            "iot_manufacturing_plant_name",
            "iot_company",
          ]),
          filters: JSON.stringify([["name", "in", plantIds]]),
          limit_page_length: 0,
        },
        skipCompanyFilter: true,
      },
    );
    const data = Array.isArray(response?.data) ? response.data : [];
    data.forEach((row: any) => {
      if (row?.name) map.set(row.name, row);
    });
  } catch (error) {
    console.warn(
      "[Machines] Falha ao buscar IoT Manufacturing Plant em lote:",
      error,
    );
  }
  return map;
}

// Helper local para buscar informações do setor de um device
async function getDeviceSectorInfo(deviceId: string): Promise<any> {
  try {
    const response = await httpClient.get<{ data: any[] }>(
      "/api/resource/IoT%20Device%20Sector",
      {
        params: {
          fields: JSON.stringify(["iot_device", "iot_sector", "iot_company"]),
          filters: JSON.stringify([["iot_device", "=", deviceId]]),
          limit_page_length: 1,
        },
        skipCompanyFilter: true,
      },
    );

    // List endpoints return { data: [...] } - not unwrapped by httpClient
    const data = Array.isArray(response?.data) ? response.data : [];
    if (data.length > 0) {
      return data[0];
    }

    return null;
  } catch (error) {
    return null;
  }
}

// Helper local para buscar informações da planta de um setor
async function getSectorPlantInfo(sectorId: string): Promise<any> {
  try {
    const response = await httpClient.get<{ data: any[] }>(
      "/api/resource/IoT%20Sector",
      {
        params: {
          fields: JSON.stringify([
            "name",
            "iot_sector_name",
            "iot_manufacturing_plant",
          ]),
          filters: JSON.stringify([["name", "=", sectorId]]),
          limit_page_length: 1,
        },
        skipCompanyFilter: true,
      },
    );

    // List endpoints return { data: [...] } - not unwrapped by httpClient
    const data = Array.isArray(response?.data) ? response.data : [];
    if (data.length > 0) {
      return data[0];
    }

    return null;
  } catch (error) {
    return null;
  }
}

// Helper local para buscar informações de uma planta manufatureira
async function getPlantInfo(plantId: string): Promise<any> {
  try {
    const response = await httpClient.get<{
      data: any[];
    }>("/api/resource/IoT%20Manufacturing%20Plant", {
      params: {
        fields: JSON.stringify([
          "name",
          "iot_manufacturing_plant_name",
          "iot_company",
        ]),
        filters: JSON.stringify([["name", "=", plantId]]),
        limit_page_length: 1,
      },
      skipCompanyFilter: true,
    });

    if (
      response?.data &&
      Array.isArray(response.data) &&
      response.data.length > 0
    ) {
      return response.data[0];
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Função CENTRALIZADA para calcular disponibilidade.
 * Garante que a lógica seja IDÊNTICA entre o serviço e o modal.
 *
 * @param deviceDataRecords - Array de Device Data com { value, seconds }
 * @param currentDeviceState - Estado atual { value, date }
 * @param lastDeviceDataDate - Data do último Device Data (ou null)
 * @param periodStart - Data de início do período
 * @param periodEnd - Data de fim do período
 * @returns { workingSeconds, totalSeconds, percentage }
 */
export function calculateAvailabilityUnified(
  deviceDataRecords: Array<{ value: string; seconds: number }>,
  currentDeviceState: { value: string; date: Date } | null,
  lastDeviceDataDate: Date | null,
  periodStart: Date,
  periodEnd: Date,
  source: string = "unknown",
): { workingSeconds: number; totalSeconds: number; percentage: number } {
  let workingSeconds = 0;

  // 1. Contar segundos de Device Data onde value = "TRABALHANDO" ou "ACTIVE"
  deviceDataRecords.forEach((record) => {
    const status = record.value?.toUpperCase();
    if (status === "TRABALHANDO" || status === "ACTIVE") {
      workingSeconds += record.seconds || 0;
    }
  });

  // 2. Se Device State é TRABALHANDO/ACTIVE, adicionar tempo até periodEnd
  if (currentDeviceState) {
    const stateValue = currentDeviceState.value?.toUpperCase();
    if (stateValue === "TRABALHANDO" || stateValue === "ACTIVE") {
      const deviceStateDate = currentDeviceState.date;

      // Determinar de onde começar a contar
      let startDate: Date;
      if (lastDeviceDataDate && lastDeviceDataDate > periodStart) {
        // Se existe Device Data recente, contar DEPOIS dele
        startDate = lastDeviceDataDate;
      } else {
        // Caso contrário, contar desde Device State.date (respeitando periodStart)
        startDate = deviceStateDate > periodStart ? deviceStateDate : periodStart;
      }

      // Adicionar segundos desde startDate até periodEnd (se válido)
      if (periodEnd > startDate) {
        const additionalSeconds = Math.floor(
          (periodEnd.getTime() - startDate.getTime()) / 1000,
        );
        workingSeconds += Math.max(0, additionalSeconds);
      }
    }
  }

  // 3. Calcular disponibilidade
  const totalSeconds = Math.floor(
    (periodEnd.getTime() - periodStart.getTime()) / 1000,
  );
  const availability =
    totalSeconds > 0 ? (workingSeconds / totalSeconds) * 100 : 0;
  const percentage = Math.floor(Math.min(100, Math.max(0, availability)));

  return { workingSeconds, totalSeconds, percentage };
}

export const machineService = {
  // Buscar TODAS as plantas da API
  async getAllPlants(companyId?: string) {
    try {
      const filters: any[] = [];
      if (companyId) {
        filters.push(["iot_company", "=", companyId]);
      }

      const response = await httpClient.get<any>(
        "/api/resource/IoT%20Manufacturing%20Plant",
        {
          params: {
            fields: JSON.stringify([
              "name",
              "iot_manufacturing_plant_name",
              "iot_company",
            ]),
            ...(filters.length > 0 && {
              filters: JSON.stringify(filters),
            }),
            limit_page_length: 0,
          },
        },
      );

      if (!response?.data || !Array.isArray(response.data)) {
        return [];
      }

      return response.data.map((plant: any) => ({
        id: plant.name,
        name: plant.iot_manufacturing_plant_name || plant.name,
      }));
    } catch (error) {
      console.error("Erro ao buscar plantas:", error);
      return [];
    }
  },

  // Buscar TODOS os setores da API
  async getAllSectors(companyId?: string) {
    try {
      const filters: any[] = [];
      if (companyId) {
        filters.push(["iot_company", "=", companyId]);
      }

      const response = await httpClient.get<any>("/api/resource/IoT%20Sector", {
        params: {
          fields: JSON.stringify([
            "name",
            "iot_sector_name",
            "iot_manufacturing_plant",
            "iot_company",
          ]),
          ...(filters.length > 0 && {
            filters: JSON.stringify(filters),
          }),
          limit_page_length: 0,
        },
      });

      if (!response?.data || !Array.isArray(response.data)) {
        return [];
      }

      return response.data.map((sector: any) => ({
        id: sector.name,
        name: sector.iot_sector_name || sector.name,
        plantId: sector.iot_manufacturing_plant,
      }));
    } catch (error) {
      console.error("Erro ao buscar setores:", error);
      return [];
    }
  },

  // Buscar todas as máquinas da API externa com filtro por company.
  // `onPartial`: callback opcional chamado assim que Device + Device State
  // resolverem (antes de resolver setor/planta), permitindo render imediato
  // dos cards de status (Em Produção / Parada / Offline).
  async getAllFromExternalAPI(
    company?: string,
    onPartial?: (machines: Machine[]) => void,
    onAvailability?: (serial: string, value: number) => void,
    onAvailabilityComplete?: () => void,
  ) {
    try {
      const startTime = performance.now();

      // ✅ Construir filtros condicionais baseado na empresa selecionada
      // ✅ IMPORTANTE: Adicionar filtro "disabled" = 0 para excluir máquinas desabilitadas
      const deviceFilters = [["disabled", "=", "0"]];
      if (company) {
        deviceFilters.push(["iot_company", "=", company]);
      }

      // SEM filtro de iot_company aqui: Device State carregava a empresa ANTIGA
      // de device realocado (campo Data congelado no insert) e o filtro derrubava
      // o status de todas as máquinas para "offline". O isolamento por empresa é
      // do servidor (permission_query_conditions); o cruzamento por device.name
      // abaixo já limita ao que a lista de Devices (essa sim filtrada) contém.
      const stateFilters = [["default_tag", "in", ["DFT0001", "DFT0023"]]];

      const results = await Promise.allSettled([
        httpClient.get<any>("/api/resource/Device", {
          params: {
            fields: JSON.stringify(["*"]),
            filters:
              deviceFilters.length > 0
                ? JSON.stringify(deviceFilters)
                : undefined,
            limit_page_length: 0,
          },
          skipCompanyFilter: true,
        }),
        httpClient.get<any>("/api/resource/Device%20State", {
          params: {
            fields: JSON.stringify(["device", "default_tag", "value"]),
            filters: JSON.stringify(stateFilters),
            limit_page_length: 0,
          },
          skipCompanyFilter: true,
        }),
      ]);

      const requestTime = performance.now() - startTime;

      // Extract results, handling failures gracefully
      const devicesRes =
        results[0].status === "fulfilled" ? results[0].value : null;
      const statesRes =
        results[1].status === "fulfilled" ? results[1].value : null;

      if (results[0].status === "rejected") {
        console.warn(
          "⚠️ Falha ao buscar Device (app continuará com lista vazia):",
          results[0].reason,
        );
      }

      if (results[1].status === "rejected") {
        console.warn(
          "⚠️ Falha ao buscar Device State (app continuará sem status):",
          results[1].reason,
        );
      }

      const devicesResult = devicesRes;
      const statesResult = statesRes;

      if (!devicesResult || !Array.isArray(devicesResult.data)) {
        console.warn(
          "⚠️ Formato de resposta inválido ou sem dados de dispositivos, retornando lista vazia",
        );
        return [];
      }

      const deviceStatusMap = new Map<string, string>();
      const deviceAngleMap = new Map<string, number>();

      if (statesResult?.data && Array.isArray(statesResult.data)) {
        statesResult.data.forEach((state: any) => {
          if (state.device && state.value && state.default_tag) {
            if (state.default_tag === "DFT0001") {
              deviceStatusMap.set(state.device, state.value);
            } else if (state.default_tag === "DFT0023") {
              deviceAngleMap.set(state.device, parseFloat(state.value) || 0);
            }
          }
        });
      }

      const mappedMachines: Machine[] = devicesResult.data.map(
        (device: any, index: number) => {
          const deviceStatus = deviceStatusMap.get(device.name);
          const deviceAngle = deviceAngleMap.get(device.name) || 0;
          const realStatus = this.mapDeviceStateToStatus(deviceStatus as any);
          const serial =
            device.serial || `SER-${index.toString().padStart(3, "0")}`;
          // Semeia a disponibilidade a partir do cache em memória (calculada em
          // uma carga anterior — ex.: antes de filtrar por empresa). Em cache
          // quente, o worker resolve tão rápido que seu setQueryData dispara
          // ANTES desta query (recém-rechaveada por empresa) comitar os dados;
          // esses writes viram no-op (prev === undefined) e o valor se perderia,
          // deixando o card preso em "—". Colocar o valor já na lista inicial
          // evita a corrida; o worker ainda atualiza valores expirados depois.
          const cachedAvail = this._availabilityCache.get(serial);

          return {
            id: device.name || `device-${index}`,
            name: device.description || `${t("dashboard.machine")} ${index + 1}`,
            type: device.data_model || "Desconhecido",
            serial,
            status: realStatus,
            location: "",
            manufacturer: device.device_model || "Não informado",
            model: device.device_model || "Não informado",
            device_model: device.device_model,
            company: device.iot_company,
            image: device.image || undefined,
            api_enabled: device.api_enabled,
            timeZone: device.time_zone || undefined,
            plant: { id: "", name: "" },
            sector: { id: "", name: "" },
            // null = ainda não calculada (worker preencherá após o commit)
            availability: (cachedAvail
              ? cachedAvail.value
              : null) as unknown as number,
            currentAngle: deviceAngle,
            lastUpdate: parseCarimboDoFrappe(device.modified) ?? new Date(),
            createdAt: parseCarimboDoFrappe(device.creation) ?? new Date(),
            metrics: {
              temperature: 0,
              pressure: Math.random() * 10 + 5,
              vibration: Math.random() * 3,
            },
          } as Machine;
        },
      );

      // ✅ Emitir lista BÁSICA imediatamente (Device + Device State já chegaram).
      // Permite que o Dashboard mostre os totais (Em Produção / Parada / Offline)
      // sem esperar pelas chamadas de setor/planta/disponibilidade.
      if (onPartial) {
        try {
          onPartial(mappedMachines.map((m) => ({ ...m })));
        } catch (e) {
          console.warn("[Machines] onPartial callback falhou:", e);
        }
      }

      // ✅ OTIMIZADO: Em vez de 5 requests por máquina (até centenas de requests
      // sequenciais), agora fazemos apenas 3 requests em lote totais para
      // resolver setor/planta de TODAS as máquinas. Disponibilidade é deferida.
      try {
        const deviceIds = mappedMachines.map((m) => m.id).filter(Boolean);
        const deviceSectorMap = await getAllDeviceSectorInfos(deviceIds, company);

        const sectorIds = Array.from(
          new Set(Array.from(deviceSectorMap.values()).map((v) => v.iot_sector)),
        ).filter(Boolean) as string[];
        const sectorMap = await getAllSectorsByIds(sectorIds);

        const plantIds = Array.from(
          new Set(
            Array.from(sectorMap.values())
              .map((s) => s?.iot_manufacturing_plant)
              .filter(Boolean) as string[],
          ),
        );
        const plantMap = await getAllPlantsByIds(plantIds);

        mappedMachines.forEach((machine) => {
          const ds = deviceSectorMap.get(machine.id);
          if (!ds) {
            machine.plant = { id: "unknown", name: t("labels.unknown") };
            machine.sector = { id: "unknown", name: t("labels.unknown") };
            machine.location = `${t("labels.unknown")} • ${t("labels.unknown")}`;
            return;
          }
          const sectorId = ds.iot_sector;
          const sectorInfo = sectorMap.get(sectorId);
          if (!sectorInfo) {
            machine.sector = { id: sectorId, name: sectorId };
            machine.plant = { id: "unknown", name: t("labels.unknown") };
            machine.location = `${t("labels.unknown")} • ${sectorId}`;
            return;
          }
          const plantId = sectorInfo.iot_manufacturing_plant;
          const plantInfo = plantId ? plantMap.get(plantId) : null;
          machine.sector = {
            id: sectorInfo.name,
            name: sectorInfo.iot_sector_name || sectorInfo.name,
          };
          if (plantInfo) {
            machine.plant = {
              id: plantInfo.name,
              name:
                plantInfo.iot_manufacturing_plant_name || plantInfo.name,
            };
          } else if (plantId) {
            machine.plant = { id: plantId, name: plantId };
          } else {
            machine.plant = { id: "unknown", name: t("labels.unknown") };
          }
          machine.location = `${machine.plant.name} • ${machine.sector.name}`;
        });
      } catch (err) {
        console.warn(
          "[Machines] Falha ao resolver setor/planta em lote (seguindo sem location):",
          err,
        );
      }

      // ✅ Disponibilidade é cara (2 requests por máquina) e NÃO é necessária
      // para o primeiro paint dos totais. Disparamos as buscas em PARALELO ao
      // restante da inicialização. Conforme cada máquina resolve, emitimos
      // `onAvailability(serial, value)` para o store atualizar o card de
      // "Disponibilidade Geral" progressivamente — sem segurar o paint.
      const MAX_AVAIL_CONCURRENT = 6;
      let availIdx = 0;
      const availWorker = async () => {
        while (true) {
          const i = availIdx++;
          if (i >= mappedMachines.length) return;
          const m = mappedMachines[i];
          try {
            const value = await this.fetchAvailability(
              m.serial,
              m.id,
              m.timeZone,
            );
            // IMPORTANT: do NOT mutate m.availability here. The mappedMachines
            // array is the same reference returned to the React Query cache,
            // so mutating m.availability would make the setQueryData callback
            // see the new value already in place and skip the cache update
            // ("no change — returning prev"), breaking re-renders. Let the
            // onAvailability callback (which calls setQueryData with a fresh
            // shallow-cloned machine) be the single source of truth.
            if (onAvailability) {
              try {
                onAvailability(m.serial, value);
              } catch (e) {
                console.warn(
                  "[Machines] onAvailability callback falhou:",
                  e,
                );
              }
            }
          } catch {
            // já tratado dentro de fetchAvailability
          }
        }
      };
      // Não aguardar — roda em paralelo ao retorno do getAll. Quando todos
      // os workers finalizarem (sucesso ou erro), avisa o consumidor para
      // que ele possa esconder indicadores de "calculando…".
      const availPromise = Promise.all(
        Array.from(
          { length: Math.min(MAX_AVAIL_CONCURRENT, mappedMachines.length) },
          () => availWorker(),
        ),
      );
      if (mappedMachines.length === 0) {
        try { onAvailabilityComplete?.(); } catch { /* ignore */ }
      } else {
        availPromise.finally(() => {
          try { onAvailabilityComplete?.(); } catch { /* ignore */ }
        });
      }

      return {
        data: mappedMachines,
        success: true,
        // i18n-exempt — mensagem de diagnóstico para desenvolvedor, não vai para a interface
        message: `Carregadas ${mappedMachines.length} máquinas`,
      };
    } catch (error) {
      console.error("❌ Erro ao buscar dados da API externa:", error);
      return {
        data: [],
        success: false,
        message: t("machines.connectivityError"),
      } as any;
    }
  },

  // Mapear status do Device State para status interno
  mapDeviceStateToStatus(
    deviceState: string,
  ): "production" | "stopped" | "offline" | "maintenance" {
    if (!deviceState) return "offline";

    const state = deviceState.toUpperCase();
    switch (state) {
      // Formato português
      case "TRABALHANDO":
        return "production";
      case "PARADA":
        return "stopped";
      // Formato English (API real)
      case "ACTIVE":
        return "production";
      case "IDLE":
        return "stopped";
      default:
        return "offline";
    }
  },

  // Buscar dados de disponibilidade da API
  // Simple in-memory cache for availability to avoid repeated calls
  _availabilityCache: new Map<string, { value: number; ts: number }>(),

  async fetchAvailability(
    serial: string,
    deviceId?: string,
    timeZone?: string,
  ): Promise<number> {
    try {
      const cacheKey = serial;
      const cached = this._availabilityCache.get(cacheKey);
      const nowTs = Date.now();
      const ttlMs = 5 * 60 * 1000; // 5 minutes
      if (cached && nowTs - cached.ts < ttlMs) {
        return cached.value;
      }

      // "Disponibilidade de hoje" é o dia da fábrica: a virada tem de ser a
      // meia-noite da máquina, senão o card mistura o fim de ontem no cálculo.
      const fuso = resolveTimeZone(timeZone);
      const now = new Date();
      const todayStart = startOfDayInTz(now, fuso);
      const startDate = toUtcSqlString(todayStart);

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000); // hard cap 15s

      // Buscar Device State (apenas DFT0001 para pegar o último estado)
      // Sempre usar o device ID para filtrar, não o serial ou name
      const deviceStateFilters: any[] = [["default_tag", "=", "DFT0001"]];
      if (deviceId) {
        deviceStateFilters.push(["device", "=", deviceId]);
      } else {
        // Se não temos deviceId, usar serial como fallback
        deviceStateFilters.push(["serial_device", "=", serial]);
      }

      // Run both requests in parallel to avoid sequential timeout issues
      const [deviceDataRes, deviceStateRes] = await Promise.all([
        authHttp.get<any>("/api/resource/Device%20Data", {
          params: {
            fields: JSON.stringify(["value", "seconds", "date"]),
            filters: JSON.stringify([
              ["serial_device", "=", serial],
              ["default_tag", "=", "DFT0001"],
              ["date", ">=", startDate],
            ]),
            order_by: "date asc",
            limit_page_length: 0,
          },
          signal: controller.signal as any,
          timeout: 15000,
        } as any),
        authHttp.get<any>("/api/resource/Device%20State", {
          params: {
            fields: JSON.stringify(["device", "value", "date", "changed_on"]),
            filters: JSON.stringify(deviceStateFilters),
            limit_page_length: 1,
            order_by: "date desc",
          },
          signal: controller.signal as any,
          timeout: 15000,
        } as any),
      ]).finally(() => clearTimeout(timeout));

      // Processar Device Data
      const deviceDataList = Array.isArray(deviceDataRes?.data)
        ? deviceDataRes.data
        : [];

      // Encontrar data do último Device Data
      let lastDeviceDataDate: Date | null = null;
      if (deviceDataList.length > 0) {
        lastDeviceDataDate = parseUtc(
          deviceDataList[deviceDataList.length - 1].date,
        );
      }

      // Processar Device State
      const deviceStateList = Array.isArray(deviceStateRes?.data)
        ? deviceStateRes.data
        : [];
      const stateDate =
        deviceStateList.length > 0 ? parseUtc(deviceStateList[0].date) : null;
      const currentDeviceState = stateDate
        ? { value: deviceStateList[0].value, date: stateDate }
        : null;

      // USAR FUNÇÃO CENTRALIZADA para garantir lógica idêntica
      const { workingSeconds, totalSeconds, percentage } = calculateAvailabilityUnified(
        deviceDataList.map((item: any) => ({
          value: item.value,
          seconds: item.seconds,
        })),
        currentDeviceState,
        lastDeviceDataDate,
        todayStart,
        now,
        `fetchAvailability(${serial})`,
      );

      const value = percentage;

      this._availabilityCache.set(cacheKey, { value, ts: nowTs });
      return value;
    } catch (error) {
      console.error(
        `[fetchAvailability] ERROR for ${serial}:`,
        error instanceof Error ? error.message : error,
      );
      this._availabilityCache.set(serial, { value: 0, ts: Date.now() });
      return 0;
    }
  },

  // Calcular disponibilidade baseada no status
  calculateAvailability(
    status: "production" | "stopped" | "offline" | "maintenance",
  ): number {
    const availabilityMap: Record<string, number> = {
      production: 100,
      stopped: 0,
      offline: 0,
      maintenance: 50,
    };
    return availabilityMap[status] ?? 0;
  },

  // Mapear status da API externa para status interno (método legado)
  mapStatus(
    externalStatus: string,
  ): "production" | "stopped" | "offline" | "maintenance" {
    if (!externalStatus) return "offline";

    const status = externalStatus.toLowerCase();
    switch (status) {
      case "active":
      case "running":
      case "online":
        return "production";
      case "stopped":
      case "paused":
        return "stopped";
      case "maintenance":
      case "repair":
        return "maintenance";
      default:
        return "offline";
    }
  },

  // Buscar todas as máquinas (método principal)
  async getAll(
    company?: string,
    onPartial?: (machines: Machine[]) => void,
    onAvailability?: (serial: string, value: number) => void,
    onAvailabilityComplete?: () => void,
  ) {
    // Verificar se deve usar API externa ou mock
    const useExternalAPI = import.meta.env.VITE_USE_EXTERNAL_API !== "false";

    if (!useExternalAPI) {
      return {
        data: [],
        success: false,
        // i18n-exempt — mensagem de diagnóstico para desenvolvedor, não vai para a interface
        message: "API externa está desabilitada - nenhuma máquina será exibida",
      };
    }

    // Tentar buscar da API externa com retry para HTTP 503

    const maxRetries = 2;
    const retryDelay = 1000; // 1 segundo

    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      try {
        const response = await this.getAllFromExternalAPI(
          company,
          onPartial,
          onAvailability,
          onAvailabilityComplete,
        );
        return response;
      } catch (error) {
        const isRetryableError =
          (error instanceof Error && /HTTP\s*50[234]/.test(error.message)) ||
          (error instanceof Error && error.name === "AbortError");

        if (isRetryableError && attempt <= maxRetries) {
          console.warn(
            `[getAll] Tentativa ${attempt} falhou, aguardando ${retryDelay}ms antes de retry...`,
          );
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          continue;
        }

        // Para outros erros, rethrow para que chamadores (stores) possam tratar/exibir o erro
        console.error("��� getAll: erro ao usar API externa:", error);
        throw error;
      }
    }
  },

  // Método original como fallback
  async getAllMock() {
    return {
      data: [],
      success: false,
      // i18n-exempt — mensagem de diagnóstico para desenvolvedor, não vai para a interface
      message: "Dados mock foram removidos - apenas API externa é suportada",
    };

    return await get<Machine[]>("/machines");
  },

  // Buscar máquina por ID
  async getById(id: string): Promise<{ data: Machine }> {
    try {
      // Device, Device State e a info de setor dependem apenas do `id` (já
      // disponível) — disparamos os três em paralelo em vez de encadear, o que
      // corta a cascata de requests da abertura dos detalhes.
      const [response, stateRes, deviceSectorInfo] = await Promise.all([
        httpClient.get<any>("/api/resource/Device", {
          params: {
            fields: JSON.stringify(["*"]),
            filters: JSON.stringify([
              ["disabled", "=", "0"],
              ["name", "=", id],
            ]),
            limit_page_length: 1,
          },
          skipCompanyFilter: true,
        }),
        httpClient.get<any>("/api/resource/Device%20State", {
          params: {
            fields: JSON.stringify(["device", "default_tag", "value"]),
            filters: JSON.stringify([
              ["device", "=", id],
              ["default_tag", "in", ["DFT0001", "DFT0023"]],
            ]),
            limit_page_length: 0,
          },
          skipCompanyFilter: true,
        }),
        getDeviceSectorInfo(id).catch((error) => {
          console.warn(`[getById] Erro ao buscar setor para ${id}:`, error);
          return null;
        }),
      ]);

      const devices = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      if (devices.length === 0) {
        throw new Error(t("machines.notFoundById", { id }));
      }

      const device = devices[0];

      const deviceStatusMap = new Map<string, string>();
      const deviceAngleMap = new Map<string, number>();

      if (stateRes?.data && Array.isArray(stateRes.data)) {
        stateRes.data.forEach((state: any) => {
          if (state.device && state.value && state.default_tag) {
            if (state.default_tag === "DFT0001") {
              deviceStatusMap.set(state.device, state.value);
            } else if (state.default_tag === "DFT0023") {
              deviceAngleMap.set(state.device, parseFloat(state.value) || 0);
            }
          }
        });
      }

      const deviceStatus = deviceStatusMap.get(id);
      const deviceAngle = deviceAngleMap.get(id) || 0;
      const realStatus = this.mapDeviceStateToStatus(deviceStatus as any);

      // Resolver plant/sector — só a cadeia setor→planta é sequencial (cada um
      // depende do id do anterior); a info de setor já veio no batch acima.
      let plant = { id: "unknown", name: t("labels.unknown") };
      let sector = { id: "unknown", name: t("labels.unknown") };
      let location = `${t("labels.unknown")} • ${t("labels.unknown")}`;

      try {
        if (deviceSectorInfo) {
          const sectorId = deviceSectorInfo.iot_sector;
          const sectorInfo = await getSectorPlantInfo(sectorId);
          if (sectorInfo) {
            const plantId = sectorInfo.iot_manufacturing_plant;
            const plantInfo = await getPlantInfo(plantId);
            if (plantInfo) {
              plant = {
                id: plantInfo.name,
                name: plantInfo.iot_manufacturing_plant_name || plantInfo.name,
              };
              sector = {
                id: sectorInfo.name,
                name: sectorInfo.iot_sector_name || sectorInfo.name,
              };
              location = `${plant.name} • ${sector.name}`;
            } else {
              plant = { id: plantId, name: plantId };
              sector = {
                id: sectorId,
                name: sectorInfo.iot_sector_name || sectorId,
              };
              location = `${plantId} • ${sectorInfo.iot_sector_name || sectorId}`;
            }
          } else {
            sector = { id: sectorId, name: sectorId };
            plant = { id: "unknown", name: t("labels.unknown") };
            location = `${t("labels.unknown")} • ${sectorId}`;
          }
        }
      } catch (error) {
        console.warn(
          `[getById] Erro ao buscar plant/sector para ${id}:`,
          error,
        );
      }

      // Disponibilidade NÃO é buscada aqui: a tela de detalhes não exibe esse
      // número e o cálculo (fetchAvailability) varre todo o Device Data DFT0001
      // do dia com timeout de 15s — era o gargalo que travava a abertura da
      // página. O painel de disponibilidade do dashboard já obtém esse dado
      // pelo fetch histórico próprio. A lista de máquinas continua usando
      // fetchAvailability via o worker em getAll().
      const availability = null as unknown as number;

      const machine: Machine = {
        id: device.name || id,
        name: device.description || `Máquina ${id}`,
        type: device.data_model || "Desconhecido",
        serial: device.serial || id,
        status: realStatus,
        location,
        manufacturer: device.device_model || "Não informado",
        model: device.device_model || "Não informado",
        device_model: device.device_model,
        company: device.iot_company,
        image: device.image || undefined,
        api_enabled: device.api_enabled,
        timeZone: device.time_zone || undefined,
        plant,
        sector,
        availability,
        currentAngle: deviceAngle,
        lastUpdate: parseCarimboDoFrappe(device.modified) ?? new Date(),
        createdAt: parseCarimboDoFrappe(device.creation) ?? new Date(),
        metrics: {
          timestamp: new Date(),
          temperature: 0,
          pressure: Math.random() * 10 + 5,
          vibration: Math.random() * 3,
        },
      };

      return { data: machine };
    } catch (error) {
      console.error(`[getById] Erro ao buscar máquina ${id}:`, error);
      throw error;
    }
  },

  // Criar nova máquina
  async create(machineData: Omit<Machine, "id" | "lastUpdate">) {
    return await post<Machine>("/machines", machineData);
  },

  // Atualizar máquina
  async update(id: string, updates: Partial<Machine>) {
    return await put<Machine>(`/machines/${id}`, updates);
  },

  // Deletar máquina
  async delete(id: string) {
    return await del(`/machines/${id}`);
  },

  // Buscar dados em tempo real
  async getRealTimeData() {
    return {
      data: [],
      success: false,
      message:
        // i18n-exempt — mensagem de diagnóstico para desenvolvedor, não vai para a interface
        "Dados mock foram removidos - implementar integração com API real",
    };
  },

  // Buscar dados de uma máquina específica em tempo real
  async getRealTimeDataById(id: string) {
    return {
      data: null,
      success: false,
      message:
        // i18n-exempt — mensagem de diagnóstico para desenvolvedor, não vai para a interface
        "Dados mock foram removidos - implementar integração com API real",
    };
  },

  // Buscar métricas hist��ricas
  async getMetrics(id: string, timeRange: string = "24h") {
    return await get<MachineMetrics[]>(`/machines/${id}/metrics`, {
      params: { timeRange },
    });
  },

  // Buscar histórico de status
  async getStatusHistory(id: string, timeRange: string = "24h") {
    return await get<
      Array<{
        timestamp: string;
        status: string;
        duration: number;
      }>
    >(`/machines/${id}/status-history`, {
      params: { timeRange },
    });
  },

  // Iniciar/parar máquina
  async startMachine(id: string) {
    return await post(`/machines/${id}/start`);
  },

  async stopMachine(id: string) {
    return await post(`/machines/${id}/stop`);
  },

  // Colocar em manutenção
  async setMaintenance(
    id: string,
    maintenanceData: {
      reason: string;
      estimatedDuration: number;
      technician: string;
    },
  ) {
    return await post(`/machines/${id}/maintenance`, maintenanceData);
  },

  // Sair da manutenção
  async exitMaintenance(id: string) {
    return await post(`/machines/${id}/exit-maintenance`);
  },

  // Buscar tipos de m��quinas disponíveis
  async getMachineTypes() {
    return await get<
      Array<{
        id: string;
        name: string;
        category: string;
        specifications: Record<string, any>;
      }>
    >("/machines/types");
  },

  // Buscar localizações disponíveis
  async getLocations() {
    return await get<
      Array<{
        id: string;
        name: string;
        sector: string;
        coordinates?: { lat: number; lng: number };
      }>
    >("/machines/locations");
  },

  // Exportar dados
  async exportData(params: {
    machineIds?: string[];
    startDate: string;
    endDate: string;
    format: "csv" | "xlsx" | "pdf";
    includeMetrics?: boolean;
  }) {
    return await post("/machines/export", params, {
      responseType: "blob",
    });
  },

  // Buscar logs de eventos
  async getEventLogs(
    id: string,
    params?: {
      startDate?: string;
      endDate?: string;
      eventType?: string;
      limit?: number;
    },
  ) {
    return await get(`/machines/${id}/events`, { params });
  },

  // Buscar configurações da máquina
  async getConfiguration(id: string) {
    return await get(`/machines/${id}/configuration`);
  },

  // Atualizar configurações da máquina
  async updateConfiguration(id: string, config: Record<string, any>) {
    return await put(`/machines/${id}/configuration`, config);
  },

  // Calibrar máquina
  async calibrate(
    id: string,
    calibrationData: {
      parameters: Record<string, number>;
      technician: string;
      notes?: string;
    },
  ) {
    return await post(`/machines/${id}/calibrate`, calibrationData);
  },

  // Buscar relatório de performance
  async getPerformanceReport(
    id: string,
    period: "daily" | "weekly" | "monthly",
  ) {
    return await get(`/machines/${id}/performance`, {
      params: { period },
    });
  },

  // Agendar manutenção preventiva
  async schedulePreventiveMaintenance(
    id: string,
    schedule: {
      date: string;
      type: string;
      description: string;
      estimatedDuration: number;
      assignedTo: string;
    },
  ) {
    return await post(`/machines/${id}/schedule-maintenance`, schedule);
  },

  // Buscar agenda de manutenções
  async getMaintenanceSchedule(id?: string) {
    const url = id
      ? `/machines/${id}/maintenance-schedule`
      : "/machines/maintenance-schedule";
    return await get(url);
  },

  // Função para testar API externa via console
  async testExternalAPI() {
    try {
      const result = await this.getAllFromExternalAPI();
      return result;
    } catch (error) {
      console.error("❌ Teste da API externa falhou:", error);
      throw error;
    }
  },

  // Fun��ão para alternar entre API externa e mock
  async toggleAPIMode(useExternal: boolean = true) {
    if (useExternal) {
      return await this.getAllFromExternalAPI();
    } else {
      return { data: [], success: false, message: "Mock desabilitado" } as any;
    }
  },
};

// Expor funções para debug no console global
if (typeof window !== "undefined") {
  (window as any).machineServiceDebug = {
    testAPI: () => machineService.testExternalAPI(),
    useExternal: () => machineService.getAllFromExternalAPI(),
  };
}
